import { createFileRoute } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { adminListAnnouncements, adminUpsertAnnouncement, adminDeleteAnnouncement } from "@/lib/announcements.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/announcements")({
  head: () => ({ meta: [{ title: "Announcements — Admin" }] }),
  component: AnnouncementsAdmin,
});

type Ann = {
  id: string; title: string; body: string; link_url: string | null; link_label: string | null;
  audience: "all"|"bartender"|"venue"; severity: "info"|"success"|"warning";
  active: boolean; starts_at: string; ends_at: string | null;
};

function AnnouncementsAdmin() {
  const fetchList = useServerFn(adminListAnnouncements);
  const upsert = useServerFn(adminUpsertAnnouncement);
  const del = useServerFn(adminDeleteAnnouncement);
  const [items, setItems] = useState<Ann[]>([]);
  const [editing, setEditing] = useState<Partial<Ann> | null>(null);

  async function reload() {
    const r = await fetchList();
    setItems((r.items ?? []) as unknown as Ann[]);
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, []);

  async function save() {
    if (!editing) return;
    const r = await upsert({ data: {
      id: editing.id,
      title: editing.title ?? "",
      body: editing.body ?? "",
      link_url: editing.link_url || null,
      link_label: editing.link_label || null,
      audience: (editing.audience ?? "all"),
      severity: (editing.severity ?? "info"),
      active: editing.active ?? true,
      ends_at: editing.ends_at || null,
    } });
    if ("error" in r) { toast.error(r.error); return; }
    toast.success("Saved");
    setEditing(null);
    void reload();
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <BackToDashboard className="mb-4" />
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl">Announcements</h1>
          <p className="text-muted-foreground text-sm mt-1">Push a banner to all users, bartenders, or venues.</p>
        </div>
        <button onClick={() => setEditing({ audience: "all", severity: "info", active: true })}
          className="bg-accent text-accent-foreground px-4 py-2 text-sm">New</button>
      </header>

      {editing && (
        <section className="mt-6 border border-border p-4 space-y-3 bg-card">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="col-span-2">Title
              <input className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </label>
            <label className="col-span-2">Body
              <textarea className="w-full mt-1 border border-border px-2 py-1.5 bg-background min-h-[60px]"
                value={editing.body ?? ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            </label>
            <label>Link URL
              <input className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.link_url ?? ""} onChange={(e) => setEditing({ ...editing, link_url: e.target.value })} />
            </label>
            <label>Link label
              <input className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.link_label ?? ""} onChange={(e) => setEditing({ ...editing, link_label: e.target.value })} />
            </label>
            <label>Audience
              <select className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.audience ?? "all"} onChange={(e) => setEditing({ ...editing, audience: e.target.value as Ann["audience"] })}>
                <option value="all">All</option>
                <option value="bartender">Bartenders</option>
                <option value="venue">Venues</option>
              </select>
            </label>
            <label>Severity
              <select className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.severity ?? "info"} onChange={(e) => setEditing({ ...editing, severity: e.target.value as Ann["severity"] })}>
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
              </select>
            </label>
            <label>Ends at (optional)
              <input type="datetime-local" className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.ends_at ? editing.ends_at.slice(0, 16) : ""}
                onChange={(e) => setEditing({ ...editing, ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={editing.active ?? true} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
              Active
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm border border-border">Cancel</button>
            <button onClick={save} className="px-3 py-1.5 text-sm bg-accent text-accent-foreground">Save</button>
          </div>
        </section>
      )}

      <ul className="mt-6 divide-y divide-border border border-border">
        {items.length === 0 && <li className="p-4 text-sm text-muted-foreground">No announcements yet.</li>}
        {items.map((a) => (
          <li key={a.id} className="p-4 flex justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">{a.title} {!a.active && <span className="text-xs text-muted-foreground">(inactive)</span>}</p>
              <p className="text-xs text-muted-foreground mt-1 truncate">{a.body}</p>
              <p className="text-[10px] uppercase tracking-wide-luxury text-muted-foreground/70 mt-1">{a.audience} · {a.severity}{a.ends_at ? ` · ends ${new Date(a.ends_at).toLocaleDateString()}` : ""}</p>
            </div>
            <div className="flex gap-3 items-start text-xs">
              <button onClick={() => setEditing(a)} className="underline">Edit</button>
              <button onClick={async () => { if (confirm("Delete?")) { await del({ data: { id: a.id } }); void reload(); } }} className="text-destructive">Delete</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
